import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useRef } from "react";
import { getUserProfile, updateUserProfile, updateUserProfileNameAndAvatar } from "@/utils/supabase-queries";
import supabaseClient from "@/utils/supabase-browser";
import { Table } from "@/types";
import { useLoggedInUser } from "@/hooks/useLoggedInUser";
import { useUserSafe } from "@/contexts/AuthContext";
import { uploadPublicUserAvatar } from "@/utils/supabase-storage-queries";
import { DEMO_USER_EMAIL, DEMO_USER_ID } from "@/constants";
import { isDemoUser } from "@/utils/demo-helpers";

// Demo user profile fallback
const DEMO_USER_PROFILE = {
  id: DEMO_USER_ID,
  email: DEMO_USER_EMAIL,
  full_name: "Demo User",
  avatar_url: null,
  enabled: true,
  created_at: new Date().toISOString()
} as unknown as Table<"user_profiles">;
export const useUserProfile = initialData => {
  const $ = _c(7);
  const user = useLoggedInUser();
  const t0 = user?.id;
  let t1;
  if ($[0] !== t0) {
    t1 = ["user-profile", t0];
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== user) {
    t2 = async () => {
      if (user) {
        if (isDemoUser(user.email)) {
          return DEMO_USER_PROFILE;
        }
        const userProfile = await getUserProfile(supabaseClient, user.id);
        if (!userProfile) {
          throw new Error("Failed to fetch user profile for User ID: " + user.id);
        }
        return userProfile;
      }
      throw new Error("User is not logged in");
    };
    $[2] = user;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = !!user?.id;
  let t4;
  if ($[4] !== initialData || $[5] !== t3) {
    t4 = {
      initialData,
      staleTime: 300000,
      cacheTime: 600000,
      enabled: t3
    };
    $[4] = initialData;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return useQuery(t1, t2, t4);
};
export const useUpdateUserProfile = t0 => {
  const $ = _c(14);
  const {
    onSuccess,
    onError,
    onMutate
  } = t0;
  const user = useUserSafe();
  const queryClient = useQueryClient();
  let t1;
  if ($[0] !== user) {
    t1 = async data => {
      if (!user) {
        console.warn("[useUpdateUserProfile] No user available, skipping mutation");
        return true;
      }
      if (user.email === DEMO_USER_EMAIL) {
        return true;
      }
      return updateUserProfile(supabaseClient, user.id, data);
    };
    $[0] = user;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== onMutate) {
    t2 = () => {
      onMutate?.();
    };
    $[2] = onMutate;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== onSuccess || $[5] !== queryClient || $[6] !== user) {
    t3 = () => {
      if (user) {
        queryClient.invalidateQueries(["user-profile", user.id]);
      }
      onSuccess?.();
    };
    $[4] = onSuccess;
    $[5] = queryClient;
    $[6] = user;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== onError) {
    t4 = error => {
      onError?.(error);
    };
    $[8] = onError;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t2 || $[11] !== t3 || $[12] !== t4) {
    t5 = {
      onMutate: t2,
      onSuccess: t3,
      onError: t4
    };
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return useMutation(t1, t5);
};
export const useUpdateUserFullnameAndAvatarMutation = t0 => {
  const $ = _c(14);
  const {
    onSuccess,
    onError,
    onMutate
  } = t0;
  const user = useLoggedInUser();
  const queryClient = useQueryClient();
  const toastRef = useRef(null);
  let t1;
  if ($[0] !== user) {
    t1 = async data => {
      if (user && user.email === DEMO_USER_EMAIL) {
        return true;
      }
      return updateUserProfileNameAndAvatar(supabaseClient, user.id, data);
    };
    $[0] = user;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== onMutate) {
    t2 = () => {
      toastRef.current = toast.loading("Updating profile...");
      onMutate?.();
    };
    $[2] = onMutate;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== onSuccess || $[5] !== queryClient || $[6] !== user) {
    t3 = () => {
      toast.success("Profile updated", {
        id: toastRef.current || ""
      });
      queryClient.invalidateQueries(["user-profile", user.id]);
      onSuccess?.();
    };
    $[4] = onSuccess;
    $[5] = queryClient;
    $[6] = user;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== onError) {
    t4 = error => {
      toast.error("Failed to update profile", {
        id: toastRef.current || ""
      });
      onError?.(error);
    };
    $[8] = onError;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t2 || $[11] !== t3 || $[12] !== t4) {
    t5 = {
      onMutate: t2,
      onSuccess: t3,
      onError: t4
    };
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return useMutation(t1, t5);
};
export const useUploadUserAvatarMutation = t0 => {
  const $ = _c(10);
  const {
    onSuccess,
    onError
  } = t0;
  const user = useLoggedInUser();
  const toastRef = useRef(null);
  let t1;
  if ($[0] !== user.id) {
    t1 = async file => await uploadPublicUserAvatar(supabaseClient, user.id, file, file.name, false, {
      upsert: true
    });
    $[0] = user.id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      toastRef.current = toast.loading("Uploading avatar...");
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== onSuccess) {
    t3 = avatarUrl => {
      onSuccess?.(avatarUrl);
      toast.success("Avatar uploaded", {
        id: toastRef.current || ""
      });
    };
    $[3] = onSuccess;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== onError) {
    t4 = error => {
      onError?.(error);
      toast.error("Failed to upload avatar", {
        id: toastRef.current || ""
      });
    };
    $[5] = onError;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t3 || $[8] !== t4) {
    t5 = {
      onMutate: t2,
      onSuccess: t3,
      onError: t4
    };
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return useMutation(t1, t5);
};
export function useUpdateUserEmailMutation() {
  const $ = _c(6);
  const queryClient = useQueryClient();
  const toastRef = useRef(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = async () => {
      toastRef.current = toast.loading("Updating email...");
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== queryClient) {
    t1 = () => {
      toast.success("Email updated!", {
        id: toastRef.current || ""
      });
      toastRef.current = null;
      queryClient.invalidateQueries(["user"]);
      window.location.reload();
    };
    $[1] = queryClient;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = error => {
      toast.error(String(error), {
        id: toastRef.current || ""
      });
      toastRef.current = null;
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1) {
    t3 = {
      onMutate: t0,
      onSuccess: t1,
      onError: t2
    };
    $[4] = t1;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return useMutation(_temp, t3);
}
async function _temp(t0) {
  const {
    oldEmail,
    newEmail
  } = t0;
  if (oldEmail === DEMO_USER_EMAIL) {
    return true;
  }
  const {
    data
  } = await supabaseClient.auth.updateUser({
    email: newEmail
  });
  return data;
}